import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { db } from '@/lib/firebase/config';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';
import { OrgChartBuilder } from './OrgChartBuilder';

interface OrgNode {
  id: string;
  name: string;
  title: string;
  email: string;
  parentId: string | null;
  order: number;
}

export function OrganizationalStructureSection() {
  const [orgData, setOrgData] = useState<OrgNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const docRef = doc(db, 'landing_profile_sections', 'structure');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setOrgData(data.orgData || []);
      }
    } catch (error) {
      console.error('Error fetching organizational structure data:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل بيانات الهيكل التنظيمي',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landing_profile_sections', 'structure');
      await updateDoc(docRef, {
        orgData: orgData,
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'تم الحفظ',
        description: 'تم تحديث بيانات الهيكل التنظيمي بنجاح',
      });
    } catch (error) {
      console.error('Error saving organizational structure data:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في حفظ البيانات',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#7dda55]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">بناء الهيكل التنظيمي</h3>
          <p className="text-sm text-gray-600">
            قم ببناء الهيكل التنظيمي بشكل تفاعلي. سيتم عرضه تلقائياً على الموقع
          </p>
        </div>
        <OrgChartBuilder data={orgData} onChange={setOrgData} />
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#7dda55] hover:bg-[#6bc444]"
        >
          {saving ? (
            <>
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              جاري الحفظ...
            </>
          ) : (
            'حفظ التغييرات'
          )}
        </Button>
      </div>
    </div>
  );
}

